/* ─────────────────────────────────────────────────────────────────────────────
   LEADS — atribución del visitante y envío al CRM (INI-145).

   ESM sin imports de Next: lo usan el formulario de /contacto, el newsletter y
   las pruebas (tests/leads.test.mjs). Las funciones puras (parsear, mezclar,
   armar el payload) no tocan el navegador; las que sí, lo hacen con try/catch.

   Contrato del CRM (bison-crm, spec 012, PR #112): POST JSON a /api/leads, solo
   desde https://bisonagents.com. Campos aceptados: firstName (obligatorio),
   lastName, email (obligatorio), phone, company, message, origin, campaign y el
   campo trampa `website`. Cualquier otro campo se rechaza, así que utm_*, gclid,
   fbclid y referrer NO viajan sueltos: se resumen en `origin` (canal, ≤60) y
   `campaign` (≤100). El detalle completo va por correo (Web3Forms) y al dataLayer.
───────────────────────────────────────────────────────────────────────────── */

// Access key público de Web3Forms (diseñado para exponerse en el cliente).
// Una sola copia: antes estaba repetida en el formulario y en el newsletter.
export const WEB3FORMS_KEY = '5e9c55fa-a15b-42ad-ae15-56bb7f6baddb';

// URL completa de la function del CRM (p. ej. https://<host-del-crm>/api/leads).
// Vacía = envío apagado: el formulario sigue yendo solo por Web3Forms. Se fija en
// el build (NEXT_PUBLIC_*) cuando core-gerente confirma que la function está desplegada.
export const CRM_LEADS_URL = process.env.NEXT_PUBLIC_CRM_LEADS_URL || '';

const STORAGE_KEY = 'bison_attribution';
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
export const CLICK_ID_KEYS = ['gclid', 'fbclid'];
const MAX_VALUE = 200;

// Límites del contrato del CRM.
const LIMITS = { firstName: 80, lastName: 80, company: 120, message: 2000, origin: 60, campaign: 100 };

const clip = (value, max) => String(value ?? '').trim().slice(0, max);

/** Atribución que trae una URL de llegada: utm_*, gclid, fbclid y el referrer externo (solo el host). */
export function attributionFromUrl(search = '', referrer = '', siteHost = 'bisonagents.com') {
  const params = new URLSearchParams(search);
  const out = {};
  for (const key of [...UTM_KEYS, ...CLICK_ID_KEYS]) {
    const value = clip(params.get(key), MAX_VALUE);
    if (value) out[key] = value;
  }
  if (referrer) {
    try {
      const host = new URL(referrer).hostname.replace(/^www\./, '');
      if (host && host !== siteHost.replace(/^www\./, '')) out.referrer = host;
    } catch {
      /* referrer inválido: se ignora */
    }
  }
  return out;
}

const hasCampaign = (a) => [...UTM_KEYS, ...CLICK_ID_KEYS].some((k) => a?.[k]);

/**
 * Mezcla lo guardado con lo recién leído, dentro de la sesión: una llegada con
 * campaña (utm_* o click id) reemplaza a la anterior; una sin campaña conserva
 * la guardada; y si no había nada, queda el referrer (o vacío).
 */
export function mergeAttribution(previous, next) {
  if (hasCampaign(next)) return { ...next, ...(next.referrer ? {} : previous?.referrer ? { referrer: previous.referrer } : {}) };
  if (previous && Object.keys(previous).length) return previous;
  return next || {};
}

/** Lee la atribución de la sesión (o {}). */
export function getAttribution() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** Se llama al cargar el sitio: lee la URL y el referrer, mezcla con lo guardado y lo guarda. */
export function captureAttribution() {
  if (typeof window === 'undefined') return {};
  const merged = mergeAttribution(
    getAttribution(),
    attributionFromUrl(window.location.search, document.referrer, window.location.hostname),
  );
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    /* almacenamiento bloqueado: la atribución de esta visita se pierde, el formulario sigue */
  }
  return merged;
}

const slug = (value) => String(value || '').toLowerCase().replace(/\s+/g, '-');

/** Canal y campaña para el CRM: `origin` = «web:<fuente>/<medio>», `campaign` = utm_campaign. */
export function crmChannel(attr = {}) {
  const source = slug(attr.utm_source) || (attr.gclid ? 'google' : attr.fbclid ? 'meta' : slug(attr.referrer) || 'directo');
  const medium =
    slug(attr.utm_medium) || (attr.gclid ? 'cpc' : attr.fbclid ? 'social' : attr.referrer ? 'referral' : 'none');
  return {
    origin: `web:${source}/${medium}`.slice(0, LIMITS.origin),
    campaign: clip(attr.utm_campaign, LIMITS.campaign),
  };
}

/** Campos de atribución para el correo de Web3Forms (solo los que existen). */
export function attributionEmailFields(attr = {}) {
  const labels = {
    utm_source: 'UTM source',
    utm_medium: 'UTM medium',
    utm_campaign: 'UTM campaign',
    utm_term: 'UTM term',
    utm_content: 'UTM content',
    gclid: 'gclid',
    fbclid: 'fbclid',
    referrer: 'Referrer',
  };
  return Object.fromEntries(Object.entries(labels).filter(([k]) => attr[k]).map(([k, label]) => [label, attr[k]]));
}

/** Payload para POST /api/leads a partir del formulario de /contacto. Solo campos del contrato. */
export function buildCrmLead(form, attr = {}) {
  const parts = String(form.name || '').trim().split(/\s+/).filter(Boolean);
  const detalle = [
    form.role && `Cargo: ${form.role}`,
    form.url && `Sitio web: ${form.url}`,
    form.country && `País: ${form.country}`,
    form.archetypeLabel && `Tipo de empresa: ${form.archetypeLabel}`,
    form.planLabel && `Plan de interés: ${form.planLabel}`,
    form.msg && `Mensaje: ${form.msg}`,
  ].filter(Boolean);
  return {
    firstName: clip(parts[0], LIMITS.firstName),
    lastName: clip(parts.slice(1).join(' '), LIMITS.lastName),
    email: clip(form.email, 254),
    company: clip(form.company, LIMITS.company),
    message: clip(detalle.join('\n'), LIMITS.message),
    ...crmChannel(attr),
    website: clip(form.botcheck, 200), // campo trampa del CRM: llega vacío si quien escribe es una persona
  };
}

/**
 * Envía el lead al CRM. Nunca lanza: devuelve null si el envío está apagado,
 * true si el CRM respondió ok y false si falló (red, CORS, 4xx/5xx).
 */
export async function sendLeadToCrm(payload) {
  if (!CRM_LEADS_URL) return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(CRM_LEADS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      signal: controller.signal,
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    return res.ok && data.ok === true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
